import { Component, ElementRef, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { StatoSessioneService } from 'app/shared/services/stato-sessione.service';
import { ApiService } from '../../../shared/services/api.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ActivatedRoute, Router } from '@angular/router';
import { merge, Subject } from 'rxjs';
import { filter, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr'; // Import Subscription
import { ProgettiService } from '../../../shared/services/progetti.service';
import { SpinnerService } from '../../../shared/services/spinner.service';
import { AuthService } from '../../../shared/auth/auth.service';
import { DownloaderComponent } from './downloader/downloader.component';
import Swal from 'sweetalert2';
import { SessionPersistenceService } from '../session-manager/session-persistence.service';


@Component({
  selector: 'app-test',
  templateUrl: './test.component.html',
  styleUrls: ['./test.component.scss']
})
export class TestComponent implements OnInit, OnDestroy {
  @ViewChild('stepTesting') testing: ElementRef;
  private destroy$: Subject<void> = new Subject<void>();
  uid: string | null = null;
  hasSubtable = false;
  models: string[];
  testStyle: string;
  templates: any[];
  templateNames: any[];
  istruzioniScriviTest: string;
  testFileName: string;
  storyFileName: string;
  selectedModel: string;
  selectedTemplate: string;
  refinedStoryString: string;
  generatedTests: any[] = [];
  columns: string[] = [];
  testCount = 0;
  @ViewChild('testFileInputHtml') fileInputHtml: ElementRef<HTMLInputElement>;
  i: number;
  projectId: string;
  projectLanguage = 'en';
  isGeneratingTests = false;
  totalCriteria = 0;
  completedCriteria = 0;

  // In your TestComponent, add this property:
  filteredProgress$ = this.sessioneService.testGenerationProgress$.pipe(
    filter(progress => progress?.completedCriteria > 0)
  );

  constructor(
    private apiService: ApiService,
    protected sessioneService: StatoSessioneService,
    private modalService: NgbModal,
    private route: ActivatedRoute,
    public spinnerService: SpinnerService,
    private toastr: ToastrService,
    private router: Router,
    private authService: AuthService,
    private downloader: DownloaderComponent,
    private sessionPersistence: SessionPersistenceService
  ) { }

  ngOnInit(): void {
    this.route.params.pipe(
      takeUntil(this.destroy$)
    ).subscribe(params => {
      const projectId = params['project_id']; // Anche se lo prendi da sessioneService, è bene averlo qui per coerenza
      const sessionId = params['session_id'];

      // Logica di ricaricamento identica a StoryComponent
      if (sessionId && this.sessionPersistence.getCurrentSessionId() !== sessionId) {
        console.log(`[TestComponent] Detected session ID '${sessionId}' in URL. Reloading...`);
        this.spinnerService.show();
        this.sessionPersistence.loadSession(sessionId).subscribe({
          next: () => {
            console.log('[TestComponent] Session data reloaded successfully.');
            this.spinnerService.hide();
          },
          error: (err) => {
            console.error('[TestComponent] Failed to reload session data.', err);
            this.spinnerService.hide();
            this.toastr.error('Failed to load session data.');
            this.router.navigate(['/projects/sessions', projectId]); // Usa projectId da params qui
          }
        });
      }
    });

    this.initializeData();
    this.setupSubscriptions();
  }

  private initializeData(): void {
    this.testFileName = '';
    this.istruzioniScriviTest = '';
    this.models = ['Haiku', 'Sonnet', 'Sonnet 3.5', 'Omni', 'Gemini Flash', 'Gemini Pro'];
    this.checkForSubtable();
  }

  private setupSubscriptions(): void {
    const subscriptions = [
      this.sessioneService.testGenerationProgress$.pipe(
        tap((progress) => {
          this.isGeneratingTests = progress.completedCriteria < progress.totalCriteria;
          this.totalCriteria = progress.totalCriteria;
          this.completedCriteria = progress.completedCriteria;
        })
      ),
      this.sessioneService.refinedStoryString$.pipe(
        tap(refinedStoryString => this.refinedStoryString = refinedStoryString)
      ),
      this.sessioneService.selectedTemplate$.pipe(
        tap(template => {
          this.selectedTemplate = template;
          this.checkForSubtable();
        })
      ),
      this.sessioneService.templates$.pipe(
        tap(templates => {
          this.templates = templates;
          this.templateNames = this.templates.map(template => template.templateName);
          if (!this.selectedTemplate && this.templateNames.length > 0) {
            this.sessioneService.updateSelectedTemplate(this.templateNames[0]);
          }
          this.checkForSubtable();
        })
      ),
      this.sessioneService.fileName$.pipe(
        tap(storyFileName => this.storyFileName = storyFileName)
      ),
      this.sessioneService.testFileName$.pipe(
        tap(testFileName => this.testFileName = testFileName)
      ),
      this.sessioneService.projectId$.pipe(
        tap(projectId => this.projectId = projectId)
      ),
      this.sessioneService.projectLanguage$.pipe(
        tap(lang => this.projectLanguage = lang)
      ),
      this.sessioneService.selectedTestStyle$.pipe(
        tap(style => this.testStyle = style)
      ),
      this.sessioneService.istruzioniScriviTest$.pipe(
        tap(instruct => this.istruzioniScriviTest = instruct)
      ),
      this.authService.email$.pipe(
        tap(email => this.uid = email)
      ),
      // Keep only the minimal subscription for generated tests in parent
      /*
      this.sessioneService.generatedTests$.pipe(
        tap((generatedTests: any[]) => {
          this.generatedTests = generatedTests;
        })
      ),
      this.sessioneService.testCount$.pipe(
        tap(count => this.testCount = count)
      ),
      */
      this.sessioneService.columns$.pipe(
        tap((columns: string[]) => {
          this.columns = columns; // This might still be needed if columns are generic or also used by refined tests. Review if it can be removed.
        })
      )
    ];

    merge(...subscriptions)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        () => { },
        error => console.error('Error in TestComponent:', error)
      );
  }

  checkForSubtable(): void {
    if (this.selectedTemplate && this.templates) {
      const selectedTemplateObj = this.templates.find(template => template.templateName === this.selectedTemplate);
      if (selectedTemplateObj) {
        this.hasSubtable = this.templateHasSubtable(selectedTemplateObj);
      }
    }
  }

  templateHasSubtable(template: any): boolean {
    if (template && template.columns && Array.isArray(template.columns)) {
      return template.columns.some(column =>
        column.list && Array.isArray(column.subColumns) && column.subColumns.length > 0
      );
    }
    return false;
  }

  generateTests(testStyle: string = '') {
    this.modalService.dismissAll();

    const testParams = this.sessioneService.getCurrentTestParams();
    if (!testParams) { return; }

    if (testParams.index === 0) {
      this.spinnerService.show();
    }

    // Get all groups
    const groups = this.sessioneService.getCriteriaGroups();
    let currentGroupIndex = -1;
    let currentCriterionIndex = -1;
    let globalIndex = 0;

    // Find the group and criterion indices
    for (let groupIdx = 0; groupIdx < groups.length; groupIdx++) {
      const group = groups[groupIdx];
      for (let critIdx = 0; critIdx < group.acceptance_criteria.length; critIdx++) {
        if (globalIndex === testParams.index) {
          currentGroupIndex = groupIdx;
          currentCriterionIndex = critIdx;
          break;
        }
        globalIndex++;
      }
      if (currentGroupIndex !== -1) { break; }
    }

    const selectedTemplateObj = this.templates.find(
      template => template.templateName === this.selectedTemplate
    );
    const selectedTemplateColumns = selectedTemplateObj?.columns || [];
    const selectedTemplateDesc = selectedTemplateObj?.templateDescription || '';

    // Create a separate Subject to handle cleanup for this specific call
    const cleanup$ = new Subject<void>();
    this.apiService.generateTestCases(
      this.projectId,
      this.sessioneService.getSessionName(),
      this.uid,
      this.projectLanguage,
      selectedTemplateDesc,
      selectedTemplateColumns,
      testParams.criterionText += '\nThe generated tests must follow these constraints: ' + this.sessioneService.getConstraintDescriptions(),
      this.istruzioniScriviTest
    ).pipe(
      take(1),
      takeUntil(cleanup$),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {

        if (testParams.index === 0) {
          this.spinnerService.hide();
        }

        cleanup$.next();
        cleanup$.complete();

        const nextParams = this.sessioneService.getNextTestParams();
        if (nextParams) {
          this.sessioneService.initiateGenerateTestsWithParams(nextParams);
          this.generateTests(testStyle);
        } else {
          // All tests are generated, save the session automatically
          // Create a name based on the test or story file name
          const sessionName = `${this.testFileName || this.storyFileName || 'Session'}_${new Date().toISOString().slice(0, 10)}`;
          this.saveToBackend(sessionName);
        }
      },
      error: (error) => {
        // Existing error handling remains unchanged...

        const nextParams = this.sessioneService.getNextTestParams();
        if (nextParams) {
          this.sessioneService.initiateGenerateTestsWithParams(nextParams);
          this.generateTests(testStyle);
        }
        // Don't save on error
      }
    });
  }

  onTemplateSelected(templateName: string): void {
    this.selectedTemplate = templateName;
    this.sessioneService.updateSelectedTemplate(templateName);
    this.checkForSubtable();
  }

  downloadGeneratedTests() {
    this.downloader.saveCurrentState(
      this.uid,
      this.testFileName,
    );
  }

  downloadStory() {
  }

  private removeTestBeforeStory(fileName: string): string {
    const testBeforeStoryRegex = /Tests\W*Story/;
    if (testBeforeStoryRegex.test(fileName)) {
      return fileName.replace(/Tests\W*(?=Story)/, '');
    }
    return fileName;
  }

  // Add new method to save to backend
  saveToBackend(sessionName: string): void {
    // Use a default name if none provided
    const name = sessionName || `Session_${new Date().toISOString().slice(0, 10)}`;

    this.spinnerService.show();
    this.sessionPersistence.saveCurrentSession().subscribe({
      next: (response) => {
        console.log('State saved successfully', response);
        this.spinnerService.hide();
        this.toastr.success('Session saved successfully!');
      },
      error: (error) => {
        console.error('Error saving state:', error);
        this.spinnerService.hide();
        this.toastr.error('Failed to save session');
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
